import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Film, Building2, Calendar, Users, UserSquare2, Sparkles, FolderHeart, X, Loader2, CornerDownLeft } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Spotlight-style global search — Cmd/Ctrl+K anywhere in the app.
 * Aggregates hits from every entity via `GET /api/search?q=...`.
 * Arrow keys navigate; Enter jumps to the highlighted result.
 */
export function GlobalSearchTrigger({ className = "", forceOpen, onForceClose }) {
  const [open, setOpen] = useState(false);
  const isControlled = forceOpen !== undefined;
  const actuallyOpen = isControlled ? forceOpen : open;
  const close = () => (isControlled ? onForceClose?.() : setOpen(false));
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (!isControlled) setOpen((v) => !v);
      } else if (e.key === "Escape" && actuallyOpen) {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actuallyOpen]);
  return (
    <>
      {!isControlled && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-stone-400 hover:text-stone-100 bg-stone-900/50 hover:bg-stone-900 border border-stone-800 hover:border-stone-700 transition-colors ${className}`}
          data-testid="global-search-trigger"
        >
          <Search size={12} className="shrink-0" />
          <span className="flex-1 text-left uppercase tracking-widest">Search…</span>
          <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] bg-stone-800 text-stone-400 border border-stone-700 font-mono">
            ⌘K
          </kbd>
        </button>
      )}
      {actuallyOpen && <GlobalSearchModal onClose={close} />}
    </>
  );
}

function GlobalSearchModal({ onClose }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 2) { setData(null); return; }
    const t = setTimeout(() => {
      setLoading(true);
      api.get(`/search?q=${encodeURIComponent(trimmed)}`)
        .then((r) => setData(r.data))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  // Flatten groups → linear list for keyboard nav
  const flat = useMemo(() => {
    if (!data) return [];
    const out = [];
    const push = (group, item, icon, label, to) => out.push({ group, item, icon, label, to });
    (data.groups.clips || []).forEach((c) => push(
      "Clips", c, Film,
      [c.rider_horse_name, c.sub_label].filter(Boolean).join(" · ") || "Clip",
      `/library?search=${encodeURIComponent(c.rider_horse_name || c.sub_label || "")}`,
    ));
    (data.groups.associations || []).forEach((a) => push(
      "Associations", a, Building2, a.name, `/associations/${encodeURIComponent(a.name)}`,
    ));
    (data.groups.performances || []).forEach((p) => push(
      "Performances", p, Calendar, p.name, `/performances/${encodeURIComponent(p.name)}`,
    ));
    (data.groups.riders || []).forEach((r) => push(
      "Riders", r, Users, r.name,
      `/library?search=${encodeURIComponent(r.name)}`,
    ));
    (data.groups.horses || []).forEach((h) => push(
      "Horses", h, () => <span className="text-sm">🐎</span>, h.name,
      `/horses/${encodeURIComponent(h.name)}`,
    ));
    (data.groups.owners || []).forEach((o) => push(
      "Owners", o, UserSquare2, o.name, `/owners/${encodeURIComponent(o.name)}`,
    ));
    (data.groups.reels || []).forEach((r) => push(
      "Reels", r, Sparkles, r.name, `/reels/${r.id}`,
    ));
    (data.groups.galleries || []).forEach((g) => push(
      "Galleries", g, FolderHeart, g.name, `/galleries/${g.slug || g.id}`,
    ));
    return out;
  }, [data]);

  useEffect(() => { setCursor(0); }, [q]);

  const jumpTo = (row) => {
    if (!row) return;
    onClose();
    navigate(row.to);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(flat.length - 1, c + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); jumpTo(flat[cursor]); }
    else if (e.key === "Escape") { onClose(); }
  };

  // Grouped view for rendering
  const grouped = useMemo(() => {
    const m = new Map();
    flat.forEach((row, i) => {
      if (!m.has(row.group)) m.set(row.group, []);
      m.get(row.group).push({ ...row, idx: i });
    });
    return Array.from(m.entries());
  }, [flat]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
      data-testid="global-search-modal"
    >
      <div
        className="bg-stone-950 border border-orange-700 w-full max-w-2xl max-h-[75vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-800 shrink-0">
          <Search size={16} className="text-orange-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search everything — riders, horses, owners, clips, associations…"
            className="flex-1 bg-transparent focus:outline-none text-stone-100 placeholder:text-stone-500"
            data-testid="global-search-input"
          />
          {loading && <Loader2 size={14} className="animate-spin text-stone-500" />}
          <button
            onClick={onClose}
            className="text-stone-500 hover:text-stone-200"
            data-testid="global-search-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {q.trim().length < 2 ? (
            <div className="px-4 py-8 text-center text-stone-500 text-sm">
              Type at least 2 characters to search across the platform.
              <div className="mt-3 text-[10px] uppercase tracking-widest text-stone-600">
                ↑↓ navigate · <CornerDownLeft size={9} className="inline" /> open · esc close
              </div>
            </div>
          ) : flat.length === 0 && !loading ? (
            <div className="px-4 py-8 text-center text-stone-500 text-sm">
              No results for <span className="text-orange-400">"{q}"</span>
            </div>
          ) : (
            <div className="py-1">
              {grouped.map(([group, rows]) => (
                <div key={group}>
                  <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-stone-500 font-mono-tc">
                    {group} <span className="text-stone-700">· {rows.length}</span>
                  </div>
                  {rows.map((row) => {
                    const Icon = row.icon;
                    const active = row.idx === cursor;
                    return (
                      <button
                        key={`${group}-${row.idx}`}
                        onClick={() => jumpTo(row)}
                        onMouseEnter={() => setCursor(row.idx)}
                        className={`w-full text-left px-4 py-2 flex items-center gap-3 transition-colors ${
                          active ? "bg-orange-950/50 border-l-2 border-orange-600" : "border-l-2 border-transparent hover:bg-stone-900"
                        }`}
                        data-testid={`global-search-hit-${group.toLowerCase()}-${row.idx}`}
                      >
                        <div className="text-stone-400 shrink-0 w-4 flex justify-center">
                          <Icon size={13} />
                        </div>
                        <div className="min-w-0 flex-1 truncate text-sm">{row.label}</div>
                        {active && <CornerDownLeft size={11} className="text-orange-400 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
